import type { Reference } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import type { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { isOfType } from '@seen/core/utils'

import { Button, cn, DotSeparator, TypographyDetail } from '@seen/ui'
import { IconRefresh } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DocumentReferenceSheet } from '#src/components/document-reference/DocumentReferenceSheet.tsx'
import { IconCommunicationStatus } from '#src/components/icons/IconCommunicationStatus.tsx'
import { OrderMention } from '#src/components/OrderMention.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { useTimelineItemHashMatch } from '#src/hooks/useHashMatch.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useScrollHighlight } from '#src/hooks/useScrollHighlight.ts'

interface FaxItemProps {
  fax: ExtendedCommunication
}

export function FaxItem({ fax }: FaxItemProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedDocument, setSelectedDocument] =
    useState<ExtendedDocumentReference | null>(null)
  const patchMutation = usePatchMutation<ExtendedCommunication>()

  const hashMatch = useTimelineItemHashMatch(fax.id)
  const { ref: highlightRef, highlightClassName } =
    useScrollHighlight(hashMatch)

  const documentPayloads = fax.payload?.filter((p) => p.contentReference) ?? []
  const textPayloads = fax.payload?.filter((p) => p.contentString) ?? []
  const messageText = textPayloads.map((p) => p.contentString).join('\n\n')

  // Fall back to the source Elation letter's date when `sent` is null so old
  // faxes show their real date instead of "Unknown time".
  const faxDate = fax.sentDate ?? fax.elationLetterSentDate

  /*
   * Determine if this is a sent or received fax based on sender/recipient
   */
  const isSentFax = !!fax.sender
  const actor = fax.sender ?? fax.recipient ?? 'Unknown'
  const otherParty = isSentFax ? fax.recipient : fax.sender

  /*
   * Get associated orders from basedOn
   */
  const basedOn = fax.basedOn ?? []
  const orderReferences = basedOn.filter((ref: Reference) =>
    isOfType(ref, 'ServiceRequest')
  )

  return (
    <div
      ref={highlightRef}
      id={`item-${fax.id}`}
      className={cn(
        'flex justify-between gap-2 py-1',
        'group overflow-hidden rounded-lg',
        highlightClassName
      )}
    >
      <div className="flex w-full min-w-0 gap-2">
        <div
          className={cn([
            'ml-2.5',
            '-my-1.5 z-10 bg-background py-1.5',
            'group-last:mb-[-100rem] group-last:pb-[100rem]',
          ])}
        >
          {actor !== 'Unknown' && <Avatar profile={actor} size="icon" />}
          {actor === 'Unknown' && (
            <div className="size-5 rounded-full bg-muted" />
          )}
        </div>
        <div className={cn('flex w-full min-w-0 flex-col', 'pt-0.5')}>
          <TypographyDetail>
            {actor !== 'Unknown' && isOfType(actor, 'Practitioner') && (
              <HoverPractitioner practitioner={actor} />
            )}
            {actor !== 'Unknown' && isOfType(actor, 'Organization') && (
              <span>{referenceDisplay(actor)}</span>
            )}
            {actor === 'Unknown' && 'Unknown'}
            <span className="font-normal text-foreground-secondary">
              {' '}
              {isSentFax && otherParty && (
                <>
                  sent a fax to{' '}
                  {isOfType(otherParty, 'Practitioner') && (
                    <HoverPractitioner practitioner={otherParty} />
                  )}
                  {isOfType(otherParty, 'Organization') && (
                    <span className="font-medium text-foreground">
                      {referenceDisplay(otherParty)}
                    </span>
                  )}
                  {!isOfType(otherParty, 'Practitioner') &&
                    !isOfType(otherParty, 'Organization') && (
                      <span className="font-medium text-foreground">
                        {referenceDisplay(otherParty)}
                      </span>
                    )}
                </>
              )}
              {isSentFax && !otherParty && <>sent a fax</>}
              {!isSentFax && otherParty && (
                <>
                  received a fax from{' '}
                  {isOfType(otherParty, 'Practitioner') && (
                    <HoverPractitioner practitioner={otherParty} />
                  )}
                  {isOfType(otherParty, 'Organization') && (
                    <span className="font-medium text-foreground">
                      {referenceDisplay(otherParty)}
                    </span>
                  )}
                  {!isOfType(otherParty, 'Practitioner') &&
                    !isOfType(otherParty, 'Organization') && (
                      <span className="font-medium text-foreground">
                        {referenceDisplay(otherParty)}
                      </span>
                    )}
                </>
              )}
              {!isSentFax && !otherParty && <>received a fax</>}
              <DotSeparator />
              {faxDate && <HoverDate date={faxDate} format="datetime" />}
              {!faxDate && 'Unknown time'}
              <DotSeparator />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-default items-center">
                    <IconCommunicationStatus status={fax.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="flex flex-col gap-2">
                    <span>
                      {fax.statusReason
                        ? fax.statusReason
                        : fax.status
                          ? `Status: ${fax.status}`
                          : 'Unknown status'}
                    </span>
                    {fax.status === 'stopped' && (
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation()
                          patchMutation.mutate({
                            instance: fax,
                            mutateFn: (comm) => {
                              comm.status = 'preparation'
                              comm.statusReason = undefined
                            },
                          })
                        }}
                        className="w-full"
                      >
                        <IconRefresh className="mr-1 size-3" />
                        Retry
                      </Button>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </span>
          </TypographyDetail>

          {messageText && (
            <div className="mt-1">
              <TypographyDetail className="line-clamp-3 text-foreground-secondary">
                <RichText
                  value={messageText.replace(/\n/g, '<br />')}
                  className="whitespace-pre-wrap"
                />
              </TypographyDetail>
            </div>
          )}

          {documentPayloads.length > 0 && (
            <div className="mt-1.5">
              <TypographyDetail className="text-foreground-secondary">
                {documentPayloads.length === 1 &&
                  documentPayloads[0].contentReference && (
                    <FaxDocumentLink
                      documentReference={documentPayloads[0].contentReference}
                      setSelectedDocument={setSelectedDocument}
                    />
                  )}
                {documentPayloads.length > 1 && !isExpanded && (
                  <Button
                    variant="link"
                    size="xs"
                    onClick={() => {
                      setIsExpanded(true)
                    }}
                    className="inline-flex h-auto items-center gap-0.5 p-0"
                  >
                    {documentPayloads.length} attachments
                  </Button>
                )}
                {documentPayloads.length > 1 && isExpanded && (
                  <>
                    <Button
                      variant="link"
                      size="xs"
                      onClick={() => {
                        setIsExpanded(false)
                      }}
                      className="inline-flex h-auto items-center gap-0.5 p-0"
                    >
                      {documentPayloads.length} attachments
                    </Button>
                    <div className="mt-1 flex flex-col gap-0.5">
                      {documentPayloads.map((payload, index) => {
                        if (!payload.contentReference) {
                          return null
                        }
                        return (
                          <FaxDocumentLink
                            key={`${payload.contentReference.reference}-${index}`}
                            documentReference={payload.contentReference}
                            setSelectedDocument={setSelectedDocument}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </TypographyDetail>
            </div>
          )}

          {orderReferences.length > 0 && (
            <div className="mt-1.5">
              <TypographyDetail className="text-foreground-secondary">
                Related:{' '}
                {orderReferences.map((orderRef, index) => (
                  <span key={orderRef.reference}>
                    {index > 0 && <DotSeparator />}
                    <OrderMention
                      order={orderRef}
                      className="border-none bg-inherit decoration-1 underline-offset-[2.5px] hover:bg-inherit hover:underline"
                    />
                  </span>
                ))}
              </TypographyDetail>
            </div>
          )}
        </div>
      </div>
      <DocumentReferenceSheet
        documentReference={selectedDocument}
        open={!!selectedDocument}
        setOpen={(open) => {
          if (!open) {
            setSelectedDocument(null)
          }
        }}
        hideDelete
        hideParticipant
      />
    </div>
  )
}

function FaxDocumentLink({
  documentReference,
  setSelectedDocument,
}: {
  documentReference: { reference?: string }
  setSelectedDocument: (doc: ExtendedDocumentReference | null) => void
}): JSX.Element | null {
  const { data: doc } = useRead(ExtendedDocumentReference, documentReference)

  if (!doc) {
    return null
  }

  return (
    <button
      type="button"
      className="cursor-pointer text-left font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden"
      onClick={() => {
        setSelectedDocument(doc)
      }}
    >
      {doc.displayTitle}
    </button>
  )
}
